import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import usersApi from '@/api/identity/usersApi'
import {
  compareVersions,
  getUnseenVersions,
  maxVersion,
  versions,
} from '@/changelog/versions'
import type { ChangelogVersion } from '@/changelog/versions'
import { selectUser } from '@/store/selectors/UserSelector'
import { updateUserProfile } from '@/store/slices/user/userSlice'

// Key used by the old per-browser popup (before SCRUM-1210).
const LEGACY_STORAGE_KEY = 'changelog_last_seen_version'
// Newest version the old popup could have shown. A browser that dismissed it
// counts as having seen up to here, so those users aren't shown it again.
const LEGACY_LAST_POPUP_VERSION = '1.1.0'

const cacheKey = (userId: number) => `changelog_seen:${userId}`

const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch (error) {
    console.error('Failed to read changelog state from localStorage:', error)
    return null
  }
}

const writeStorage = (key: string, value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch (error) {
    console.error('Failed to write changelog state to localStorage:', error)
  }
}

type ChangelogUser = {
  createdAt?: string
  lastSeenChangelogVersion?: string | null
}

type ChangelogState = {
  /** Newest version the user counts as having seen; null = nothing. */
  lastSeen: string | null
  /** Version to save to the server quietly, or null when it is up to date. */
  pendingSync: string | null
}

/**
 * Works out what the user has seen. The account value is the source of truth;
 * the per-user cache covers a save that hasn't reached the server yet.
 * Users with nothing saved are either migrated from the old per-browser key
 * or, if the account is newer than the latest release, treated as up to date.
 */
export const resolveChangelogState = (
  user: ChangelogUser,
  cached: string | null,
  hasLegacyKey: boolean,
  latest: ChangelogVersion | undefined,
): ChangelogState => {
  const serverSeen = user.lastSeenChangelogVersion ?? null
  let lastSeen = maxVersion(serverSeen, cached)

  if (!lastSeen && hasLegacyKey) lastSeen = LEGACY_LAST_POPUP_VERSION

  const isNewAccount =
    !!latest &&
    !!user.createdAt &&
    new Date(user.createdAt).getTime() >= new Date(latest.date).getTime()
  if (!lastSeen && isNewAccount) lastSeen = latest.version

  const pendingSync =
    lastSeen && (!serverSeen || compareVersions(lastSeen, serverSeen) > 0)
      ? lastSeen
      : null

  return { lastSeen, pendingSync }
}

type UseChangelogReturn = {
  /** False until the user profile has loaded. */
  isReady: boolean
  latestVersion: ChangelogVersion | undefined
  /** Entries newer than what the user has seen, newest first. */
  unseenVersions: ChangelogVersion[]
  /** Unseen entries that should appear in the popup (not marked quiet). */
  popupVersions: ChangelogVersion[]
  hasUnseen: boolean
  /** Version to save quietly (migration, new account, or a failed save). */
  pendingSync: string | null
  markAsSeen: () => void
  saveSeen: (version: string) => Promise<void>
}

const useChangelog = (): UseChangelogReturn => {
  const dispatch = useDispatch()
  const user = useSelector(selectUser)
  const latestVersion = versions[0]
  const userId = user?.id
  const serverSeen = user?.lastSeenChangelogVersion ?? null

  const { lastSeen, pendingSync } = useMemo(() => {
    if (!user || !userId) return { lastSeen: null, pendingSync: null }
    return resolveChangelogState(
      user,
      readStorage(cacheKey(userId)),
      readStorage(LEGACY_STORAGE_KEY) !== null,
      latestVersion,
    )
  }, [user, userId, latestVersion])

  const unseenVersions = useMemo(
    () => (user ? getUnseenVersions(versions, lastSeen) : []),
    [user, lastSeen],
  )
  const popupVersions = useMemo(
    () => unseenVersions.filter((v) => v.popup),
    [unseenVersions],
  )

  const saveSeen = useCallback(
    async (version: string) => {
      if (!userId) return
      const next = maxVersion(serverSeen, version)
      // Cache first so a failed request is retried on the next load.
      writeStorage(cacheKey(userId), next)
      writeStorage(LEGACY_STORAGE_KEY, null)
      dispatch(updateUserProfile({ lastSeenChangelogVersion: next }))
      try {
        await usersApi.markChangelogSeen(version)
      } catch (error) {
        console.error('Failed to save changelog seen version:', error)
      }
    },
    [dispatch, serverSeen, userId],
  )

  const markAsSeen = useCallback(() => {
    if (latestVersion) void saveSeen(latestVersion.version)
  }, [latestVersion, saveSeen])

  return {
    isReady: !!user,
    latestVersion,
    unseenVersions,
    popupVersions,
    hasUnseen: unseenVersions.length > 0,
    pendingSync,
    markAsSeen,
    saveSeen,
  }
}

export default useChangelog
